import { authHeader } from "@/_helpers";
import axios from "axios";
import router from "../router";
import { tokenService } from "./token.service";

const API_TIMEOUT = 60000;

/**
 * Turn any raw axios/network error into a predictable, typed error the UI can
 * map to a friendly message. `type` is one of:
 *   "auth"    -> 401/403, bad or missing credentials
 *   "server"  -> 5xx, backend failure
 *   "client"  -> other 4xx
 *   "network" -> request sent, no response (server down/unreachable)
 *   "timeout" -> request aborted after API_TIMEOUT
 *   "unknown" -> anything else
 */
function normalizeError(error) {
  if (error && error.isApiError) return error;

  let type = "unknown";
  let status = null;
  let message = "An unexpected error occurred. Please try again.";

  if (error && error.code === "ECONNABORTED") {
    type = "timeout";
    message = "The server took too long to respond. Please try again.";
  } else if (error && error.response) {
    status = error.response.status;
    const detail =
      error.response.data &&
      (error.response.data.detail || error.response.data.message);
    if (status === 401 || status === 403) {
      type = "auth";
      message = "Incorrect username or password.";
    } else if (status >= 500) {
      type = "server";
      message = "The server encountered a problem. Please try again later.";
    } else {
      type = "client";
      message = detail || "The request could not be completed.";
    }
  } else if (error && error.request) {
    type = "network";
    message =
      "We couldn't reach the server. Check your connection and try again.";
  } else if (error && error.message) {
    message = error.message;
  }

  const apiError = new Error(message);
  apiError.isApiError = true;
  apiError.type = type;
  apiError.status = status;
  apiError.original = error;
  return apiError;
}

// Retry once after a transparent token refresh on 401
axios.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const newToken = await tokenService.refreshAccessToken();
        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        return axios(originalRequest);
      } catch {
        logout();
        router.push({ name: "login" });
      }
    }
    return Promise.reject(error);
  }
);

export const apiService = {
  editUser,
  getAll,
  postBeacon,
  logout,
  register,
  resetPassword,
  validateCode,
  setRessetPassword,
  checkCallback
};

function logout() {
  // remove user from local storage to log user out
  localStorage.removeItem("sourcelink");
  localStorage.removeItem("userData");
  localStorage.removeItem("user");
}

async function register(user) {
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    username: user.username,
    password: user.password,
    password_confirmation: user.password_confirmation,
    email: user.email,
    terms_condition: JSON.stringify(user.terms_condition)
    // params: JSON.stringify(user)
  };
  return await axios
    .post(`${process.env.VUE_APP_URL}/account/`, requestOptions)
    .then(handleResponse)
    .catch(function(error) {
      handleError(error);
    });
}

function editUser(user) {
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    username: user.username,
    email: user.email,
    first_name: user.first_name,
    last_name: user.last_name,
    pk_idusers: user.pk_idusers,
    password_old: user.password_old,
    password_new: user.password_new,
    password_new_confirm: user.password_new_confirm,
    phone: user.phone,
    Country: user.Country,
    city: user.city,
    address: user.address,
    company: user.company,
    role: user.role,
    web_page: user.web_page,
    sector: user.sector,
    Ventas: user.Ventas,
    foreign_currency_exposure: user.foreign_currency_exposure,
    type_foreign_currency_exposure: user.type_foreign_currency_exposure,
    commodity_exposure: user.commodity_exposure,
    type_commodity_exposure: user.type_commodity_exposure,
    habeas_data: user.habeas_data
  };
  return axios
    .post(`${process.env.VUE_APP_URL}/user/update`, requestOptions)
    .then(handleResponse);
}

async function resetPassword(email) {
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    email: email
  };
  return await axios
    .post(`${process.env.VUE_APP_URL}/user/resetpassword`, requestOptions)
    .then(handleResponse)
    .catch(function(error) {
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
      } else if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        console.log(error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        console.log("Error", error.message);
      }
      return ["Failed to process password reset request, Try again."];
    });
}

async function validateCode(email, code, password) {
  const requestBody = {
    email: email,
    code: code,
    password: password
  };

  return await axios
    .post(`${process.env.VUE_APP_URL}/user/validatecode`, requestBody, {
      headers: {
        "Content-Type": "application/json"
      }
    })
    .then(handleResponse)
    .catch(function(error) {
      if (error.response) {
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
      } else if (error.request) {
        console.log(error.request);
      } else {
        console.log("Error", error.message);
      }
      return ["Failed to process password reset request, Try again."];
    });
}

function setRessetPassword(user) {
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    pk_idusers: user.pk_idusers,
    password: user.password,
    password_confirmation: user.password_confirmation
  };
  return axios
    .post(`${process.env.VUE_APP_URL}/user/setRessetPassword`, requestOptions)
    .then(handleResponse);
}

var getRequest = async function(endpoint, query_params) {
  try {
    return await axios.get(`${process.env.VUE_APP_URL}/${endpoint}/`, {
      headers: authHeader(),
      params: query_params,
      timeout: API_TIMEOUT
    });
  } catch (error) {
    handleError(error);
    throw normalizeError(error);
  }
};

var postRequest = async function(endpoint, query_params, content_type) {
  const requestOptions = {
    method: "POST",
    headers: authHeader(),
    params: query_params
  };
  requestOptions.headers["Content-Type"] = content_type;
  let requestOptionsSended = {};
  switch (content_type) {
    case "multipart/form-data":
      requestOptionsSended = requestOptions.params.formData;
      break;
    case "application/json":
      requestOptionsSended = requestOptions;
      break;
    default:
      break;
  }
  return await axios
    .post(`${process.env.VUE_APP_URL}/${endpoint}/`, requestOptionsSended, {
      headers: requestOptions.headers
    })
    .then(handleResponse)
    .catch(function(error) {
      handleError(error);
    });
};

var putRequest = function(endpoint, query_params, content_type) {
  const requestOptions = {
    method: "PUT",
    headers: authHeader(),
    params: query_params
  };
  requestOptions.headers["Content-Type"] = content_type;
  let requestOptionsSended = {};
  switch (content_type) {
    case "multipart/form-data":
      requestOptionsSended = requestOptions.params.formData;
      break;
    case "application/json":
      requestOptionsSended = requestOptions;
      break;
    default:
      break;
  }
  return axios
    .put(`${process.env.VUE_APP_URL}/${endpoint}/`, requestOptionsSended, {
      headers: requestOptions.headers
    })
    .catch(function(error) {
      handleError(error);
    });
};

function handleRequest(params) {
  let data = null;
  switch (params.method) {
    case "GET":
      // Let errors propagate (typed via normalizeError) so callers can react.
      data = getRequest(params.endpoint, params.query_params).then(
        response => response.data
      );
      break;
    case "POST":
      data = postRequest(
        params.endpoint,
        params.query_params,
        params.content_type
      )
        .then(response => {
          if (response.status === 500) {
            // auto logout if 401 response returned from api
            return response.statusText;
          }
          // response.data.successfully
          //
          try {
            return response.data.successful;
          } catch (error) {
            return response;
          }
        })
        .catch(e => {
          console.error(e);
        });
      break;
    case "PUT":
      data = putRequest(
        params.endpoint,
        params.query_params,
        params.content_type
      )
        .then(response => {
          if (response.status === 500) {
            // auto logout if 401 response returned from api
            return response.statusText;
          }
          // response.data.successfully
          return response.data.successful;
        })
        .catch(e => {
          console.error(e);
        });
      break;
    default:
      break;
  }
  return data;
}

function getAll(params) {
  return handleRequest(params);
}

/**
 *sends a keepalive fetch POST that survives page unload, used as a last-ditch way to 
 *persist pending user preferences when a normal axios request would be cancelled.
 */
function postBeacon(endpoint, query_params, content_type) {
  const headers = authHeader();
  headers["Content-Type"] = content_type || "application/json";
  const body = JSON.stringify({
    method: "POST",
    headers,
    params: query_params
  });
  try {
    return fetch(`${process.env.VUE_APP_URL}/${endpoint}/`, {
      method: "POST",
      headers,
      body,
      keepalive: true
    });
  } catch (error) {
    console.error("postBeacon failed", error);
    return Promise.resolve();
  }
}

function handleError(error) {
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    console.error(error.response.data);
    console.error(error.response.status);
    console.error(error.response.headers);
    if (error.response.status === 401 || error.response.status === 403) {
      // auto logout if 401 response returned from api
      logout();
      router.push({ name: "login" });
    }
  } else if (error.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    console.error(error.request);
  } else {
    // Something happened in setting up the request that triggered an Error
    console.error("Error", error.message);
  }
  console.error(error.config);
}

function handleResponse(response) {
  const data = response.data;
  // const data = text && JSON.parse(text);
  if (!response.statusText) {
    if (response.status === 401) {
      // auto logout if 401 response returned from api
      // console.error("handleResponse(response)");
      console.error(response);
      //logout();
      //router.push("/logout");
      //location.reload(true);
    } else {
      // console.error("handleResponse(response)");
      console.error(response.status);
    }
    const error = (data && data.message) || response.statusText;
    return Promise.reject(error);
  }
  return data;
}


async function checkCallback(code, state, session_state) {
  const data = {
    code: code,
    state: state,
    session_state: session_state
  };

  const config = {
    headers: {
      "Content-Type": "application/json"
    }
  };

  console.log(data);

  return await axios
    .post(`${process.env.VUE_APP_GATEWAY_URL}auth/oidc/callback`, data, config)
    .then(handleResponse)
    .catch(handleError);
}